import type { TFunction } from 'i18next';
import { object, ObjectSchema } from 'yup';
import {
  AnyAssetApplication,
  AnyAssetSubType,
  applicationEpoValidationSchema,
  applicationWsusValidationSchema,
  assetConfigurations,
  computeAssetValidationSchema,
  controlApplicationValidationSchema,
  CustomField,
  envAssetValidationSchema,
  isaeApplicationValidationSchema,
  networkAssetValidationSchema,
  plcDcsAssetValidationSchema,
} from '@mss/mss-component-library';

type SubTypeInformation<AssetType extends AnyAssetApplication> = {
  validationSchema: (
    t: TFunction,
    customFields: CustomField[]
  ) => ObjectSchema<any>;
  skeletonObject: Record<keyof AssetType, any>;
  connectionKeys?: string[];
};

const emptySubTypeInfo: SubTypeInformation<any> = {
  validationSchema: () => object({}),
  skeletonObject: {},
};

export function assetSubTypeInformation<AssetType extends AnyAssetApplication>(
  subType: string
): SubTypeInformation<AssetType> {
  const sharedAnalyzerManagementApplication: Omit<
    SubTypeInformation<AssetType>,
    'skeletonObject'
  > = {
    validationSchema: computeAssetValidationSchema,
  };

  const sharedSecurityApplication: Omit<
    SubTypeInformation<AssetType>,
    'form'
  > = {
    skeletonObject:
      assetConfigurations.security.assets.trellix_epo!.form.initialValues,
    validationSchema: applicationEpoValidationSchema,
  };

  const sharedAssetMangementApplication: Omit<
    SubTypeInformation<AssetType>,
    'form' | 'connectionKeys' | 'validationSchema' | 'skeletonObject'
  > = {};

  const sharedNetworkAsset: Omit<SubTypeInformation<AssetType>, ''> = {
    skeletonObject:
      assetConfigurations.network.assets.firewall!.form.initialValues,
    validationSchema: networkAssetValidationSchema,
  };

  const sharedPlcDcsAsset: Omit<SubTypeInformation<AssetType>, ''> = {
    skeletonObject: assetConfigurations.plcdcs.assets.avr!.form.initialValues,
    validationSchema: plcDcsAssetValidationSchema(false),
  };

  const sharedComputeAsset: Omit<SubTypeInformation<AssetType>, ''> = {
    skeletonObject:
      assetConfigurations.compute.assets.windows_agentless!.form.initialValues,
    validationSchema: computeAssetValidationSchema,
  };

  switch (subType as AnyAssetSubType) {
    case 'microsoft_hyper_v':
      return {
        skeletonObject:
          assetConfigurations.virtualization.assets.microsoft_hyper_v!.form
            .initialValues,
        validationSchema: computeAssetValidationSchema,
      };
    case 'calsys':
      return {
        ...sharedAnalyzerManagementApplication,
        skeletonObject:
          assetConfigurations.analyzermanagement.assets.calsys!.form
            .initialValues,
      };
    case 'aaims':
      return {
        ...sharedAnalyzerManagementApplication,
        skeletonObject:
          assetConfigurations.analyzermanagement.assets.aaims!.form
            .initialValues,
      };
    case 'claroty':
    case 'trellix_epo':
    case 'nozomi':
    case 'veeam':
    case 'whatsup_gold':
      return sharedSecurityApplication;
    case 'wsus':
      return {
        ...sharedSecurityApplication,
        validationSchema: applicationWsusValidationSchema,
        skeletonObject:
          assetConfigurations.security.assets.wsus!.form.initialValues,
      };
    case 'prm':
      return {
        ...sharedAssetMangementApplication,
        validationSchema: computeAssetValidationSchema,
        skeletonObject:
          assetConfigurations.assetmanagement.assets.prm!.form.initialValues,
      };
    case 'isae':
      return {
        ...sharedAssetMangementApplication,
        validationSchema: isaeApplicationValidationSchema,
        skeletonObject:
          assetConfigurations.assetmanagement.assets.isae!.form.initialValues,
      };
    case 'centum_vp':
      return {
        skeletonObject:
          assetConfigurations.control.assets.centum_vp!.form.initialValues,
        validationSchema: controlApplicationValidationSchema,
      };

    case 'switch':
    case 'firewall':
    case 'router':
    case 'timeserver':
      return sharedNetworkAsset;
    case 'odu':
      return {
        validationSchema: envAssetValidationSchema,
        skeletonObject: assetConfigurations.env.assets.odu!.form.initialValues,
      };
    case 'fcn':
      return {
        ...sharedPlcDcsAsset,
        validationSchema: plcDcsAssetValidationSchema(true),
        skeletonObject:
          assetConfigurations.plcdcs.assets.fcn!.form.initialValues,
      };
    case 'avr':
    case 'bcv':
    case 'fcs':
    case 'scs':
    case 'wac':
      return sharedPlcDcsAsset;
    case 'siemens_s7':
      return {
        ...sharedPlcDcsAsset,
        skeletonObject:
          assetConfigurations.plcdcs.assets.siemens_s7!.form.initialValues,
      };
    case 'siemens_generic':
      return {
        ...sharedPlcDcsAsset,
        skeletonObject:
          assetConfigurations.plcdcs.assets.siemens_generic!.form.initialValues,
      };
    case 'windows_agentless':
      return sharedComputeAsset;
    default:
      return emptySubTypeInfo;
  }
}
